import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { SiteHeader } from '@/components/layout/SiteHeader';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { useDocumentMeta } from '@/lib/useDocumentMeta';

/** True when the active UI language is Japanese — legal pages render a JA variant. */
export function useIsJa(): boolean {
  const { i18n } = useTranslation();
  return (i18n.language || 'en').toLowerCase().startsWith('ja');
}

interface LegalLayoutProps {
  title: string;
  /** ISO date, e.g. "2026-05-28". Rendered as "Last updated …". */
  updated: string;
  metaTitle: string;
  metaDescription: string;
  path: string;
  children: ReactNode;
}

/**
 * Shared chrome + typography for the legal/compliance pages
 * (Privacy, Terms, DPA, Security). Content is authored in English;
 * jurisdiction-specific localized versions need separate legal review
 * before they ship, so these pages intentionally don't run through the
 * per-locale i18n leaf system the rest of the app uses.
 */
export function LegalLayout({
  title,
  updated,
  metaTitle,
  metaDescription,
  path,
  children,
}: LegalLayoutProps) {
  useDocumentMeta({ title: metaTitle, description: metaDescription, path });
  const isJa = useIsJa();
  return (
    <div className="min-h-screen">
      <SiteHeader />
      <main className="mx-auto max-w-3xl px-6 pt-20 pb-24">
        <h1 className="display text-4xl font-semibold tracking-tight text-ink-900">{title}</h1>
        <p className="mt-2 text-sm text-ink-500">
          {isJa ? `最終更新日 ${updated}` : `Last updated ${updated}`}
        </p>
        <div className="legal mt-10 space-y-6 text-[15px] leading-relaxed text-ink-700">
          {children}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

/** Section heading + body wrapper used inside the legal pages. */
export function LegalSection({ heading, children }: { heading: string; children: ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold text-ink-900">{heading}</h2>
      {children}
    </section>
  );
}
